import { DecimalPipe } from '@angular/common';
import { Component, OnInit, OnDestroy, LOCALE_ID, inject } from '@angular/core';
import {
  MatTableDataSource,
  MatTable,
  MatColumnDef,
  MatHeaderCellDef,
  MatHeaderCell,
  MatCellDef,
  MatCell,
  MatFooterCellDef,
  MatFooterCell,
  MatHeaderRowDef,
  MatHeaderRow,
  MatRowDef,
  MatRow,
  MatFooterRowDef,
  MatFooterRow,
} from '@angular/material/table';
import { NotificationEmptyComponent } from '@next/core-lib/components';
import { LocaleService, TranslatePipe } from '@next/core-lib/i18n';
import { Store } from '@ngrx/store';

import { Subject } from 'rxjs';
import { filter, takeUntil } from 'rxjs/operators';

import { getAttentionLimitsByDate } from '../../store/selectors/attention-limits.selectors';
import { getProductionSummary } from '../../store/selectors/production.selectors';
import { SiteState } from './../../../shared/models';

@Component({
  selector: 'app-attention-summary',
  templateUrl: './attention-summary.component.html',
  styleUrls: ['./attention-summary.component.scss'],
  imports: [
    MatTable,
    MatColumnDef,
    MatHeaderCellDef,
    MatHeaderCell,
    MatCellDef,
    MatCell,
    MatFooterCellDef,
    MatFooterCell,
    MatHeaderRowDef,
    MatHeaderRow,
    MatRowDef,
    MatRow,
    MatFooterRowDef,
    MatFooterRow,
    NotificationEmptyComponent,
    DecimalPipe,
    TranslatePipe,
  ],
})
export class AttentionSummaryComponent implements OnInit, OnDestroy {
  private readonly store = inject<Store<SiteState>>(Store);
  private readonly locale = inject(LOCALE_ID);
  private readonly localeService = inject(LocaleService);

  public displayedColumns: string[] = [
    'groupName',
    'numberOfAnimals',
    'fatPercentageLowerThenProtein',
    'maximumFatProteinPercentage',
    'ketose',
    'minimumProteinPercentage',
    'decreasePercentageKgMilk',
  ];
  public dataSource = new MatTableDataSource<IAttentionSummary>();
  public footer: IAttentionSummary;
  public minimumProteinPercentageLabel: string;
  public maximumFatProteinPercentageLabel: string;

  private productionSummary$ = this.store.select(getProductionSummary);
  private getAttentionLimitsByDate$ = this.store.select(getAttentionLimitsByDate);
  private readonly destroy$ = new Subject<void>();

  ngOnInit() {
    this.productionSummary$
      .pipe(
        filter((productionSummary) => !!productionSummary),
        takeUntil(this.destroy$),
      )
      .subscribe((productionSummary) => {
        const tableData: IAttentionSummary[] = productionSummary.groups.map((g) => ({
          groupName: this.localeService.translate(
            `dairyresults.production.productsummary.group-${g.name}`,
          ),
          numberOfAnimals: g.attentions.numberOfAnimals,
          fatPercentageLowerThenProtein: `${Math.round(
            g.attentions.fatPercentageLowerThenProteinPercentagePercentage,
          )}%
            (${g.attentions.fatPercentageLowerThenProteinPercentageCount})`,
          maximumFatProteinPercentage: `${Math.round(
            g.attentions.maximumFatProteinPercentagePercentage,
          )}%
            (${g.attentions.maximumFatProteinPercentageCount})`,
          ketose: `${Math.round(g.attentions.ketosePercentage)}%
            (${g.attentions.ketoseCount})`,
          minimumProteinPercentage: `${Math.round(g.attentions.minimumProteinPercentagePercentage)}%
            (${g.attentions.minimumProteinPercentageCount})`,
          decreasePercentageKgMilk: `${Math.round(g.attentions.decreasePercentageKgMilkPercentage)}%
            (${g.attentions.decreasePercentageKgMilkCount})`,
        }));
        this.footer = tableData.pop();
        tableData.splice(3, 0, {});
        this.dataSource.data = tableData;
      });
    this.getAttentionLimitsByDate$
      .pipe(
        filter((result) => !!result),
        takeUntil(this.destroy$),
      )
      .subscribe((result) => {
        const minimumProteinPercentage = new DecimalPipe(this.locale).transform(
          result.attentionLimits.production.minimumProteinPercentage,
          '1.2-2',
        );
        const maximumFatProteinPercentage = new DecimalPipe(this.locale).transform(
          result.attentionLimits.production.maximumFatProteinPercentage,
          '1.2-2',
        );

        this.minimumProteinPercentageLabel = this.localeService.translate(
          `dairyresults.attention-summary.minimumProteinPercentage`,
          { value: minimumProteinPercentage },
        );
        this.maximumFatProteinPercentageLabel = this.localeService.translate(
          `dairyresults.attention-summary.maximumFatProteinPercentage`,
          { value: maximumFatProteinPercentage },
        );
      });
  }

  ngOnDestroy(): void {
    this.destroy$.next();
    this.destroy$.complete();
  }

  public hasData = (): boolean => this.dataSource.data.length > 0;
}
interface IAttentionSummary {
  groupName?: string;
  numberOfAnimals?: number;
  fatPercentageLowerThenProtein?: string;
  maximumFatProteinPercentage?: string;
  ketose?: string;
  minimumProteinPercentage?: string;
  decreasePercentageKgMilk?: string;
}
